import React,{useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiSend,FiCheck,FiAlertCircle,FiMail,FiMessageSquare,FiUser,FiGlobe}=FiIcons;

const ReportForm=()=> {
  const [formData,setFormData]=useState({
    name: '',
    email: '',
    abuseType: '',
    description: '',
    evidence: ''
  });
  const [isSubmitting,setIsSubmitting]=useState(false);
  const [isSubmitted,setIsSubmitted]=useState(false);
  const [error,setError]=useState('');

  const abuseTypes=[
    'Spam E-Mail',
    'Phishing',
    'Malware',
    'Urheberrechtsverletzung',
    'Betrug',
    'Netzwerkangriff',
    'Andere'
  ];

  const handleSubmit=async (e)=> {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Create mailto link with all form data
      const subject=encodeURIComponent(`Abuse Meldung: ${formData.abuseType} von ${formData.name}`);
      const body=encodeURIComponent(
        `Abuse Meldung (MX2.Heinsberg.org)\n\n` +
        `Name: ${formData.name}\n` +
        `E-Mail: ${formData.email}\n` +
        `Art des Missbrauchs: ${formData.abuseType}\n\n` +
        `Beschreibung:\n${formData.description}\n\n` +
        `Beweise/URLs:\n${formData.evidence}\n\n` +
        `---\n` +
        `Gesendet am: ${new Date().toLocaleString('de-DE')}\n`
      );

      // Open email client
      window.location.href=`mailto:abuse@wasacon.net?subject=${subject}&body=${body}`;
      
      // Small delay
      await new Promise(resolve=> setTimeout(resolve,1000));
      setIsSubmitted(true);
    } catch (err) {
      setError('Beim Senden der E-Mail ist ein Fehler aufgetreten.');
      console.error('Form submission error:',err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange=(e)=> {
    setFormData({...formData,[e.target.name]: e.target.value});
  };

  if (isSubmitted) {
    return (
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-2xl">
          <motion.div 
            initial={{scale: 0,opacity: 0}}
            animate={{scale: 1,opacity: 1}}
            transition={{type: "spring",stiffness: 200}}
            className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-md border border-emerald-500/30 rounded-3xl p-12 text-center"
          >
            <motion.div 
              initial={{scale: 0}}
              animate={{scale: 1}}
              transition={{delay: 0.2,type: "spring",stiffness: 300}}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <SafeIcon icon={FiCheck} className="w-10 h-10 text-white" />
            </motion.div>
            <h2 className="text-3xl font-bold text-white mb-4">E-Mail Client geöffnet!</h2>
            <p className="text-gray-300 text-lg mb-4">
              Ihr Standard-E-Mail-Programm wurde mit den Details vorbereitet.
            </p>
            <p className="text-gray-400 text-sm">
              Bitte bestätigen Sie das Senden an <span className="text-emerald-400 font-medium">abuse@wasacon.net</span>.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 px-6">
      <div className="container mx-auto max-w-4xl">
        <motion.div 
          initial={{y: 50,opacity: 0}}
          animate={{y: 0,opacity: 1}}
          transition={{duration: 0.8}}
          className="bg-slate-900/50 backdrop-blur-md border border-blue-500/20 rounded-3xl p-8 md:p-12 shadow-2xl shadow-blue-900/10"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent mb-4">
              Missbrauch Melden
            </h2>
            <p className="text-gray-400 text-lg">
              Sichere Übermittlung an das MX2 Security Team (Visio Wasacon GmbH)
            </p>
          </div>

          {error && (
            <motion.div 
              initial={{opacity: 0,y: -10}}
              animate={{opacity: 1,y: 0}}
              className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300 text-sm"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid md:grid-cols-2 gap-8">
              <motion.div whileFocus={{scale: 1.02}} transition={{type: "spring",stiffness: 300}}>
                <label className="block text-gray-300 font-medium mb-3 flex items-center space-x-2">
                  <SafeIcon icon={FiUser} className="w-5 h-5 text-blue-400" />
                  <span>Name</span>
                </label>
                <input 
                  type="text" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                  className="w-full bg-slate-950/50 border border-slate-700 rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-blue-400 focus:bg-slate-900/80 transition-all duration-300"
                  placeholder="Ihr Name"
                />
              </motion.div>
              <motion.div whileFocus={{scale: 1.02}} transition={{type: "spring",stiffness: 300}}>
                <label className="block text-gray-300 font-medium mb-3 flex items-center space-x-2">
                  <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-400" />
                  <span>E-Mail</span>
                </label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                  className="w-full bg-slate-950/50 border border-slate-700 rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-blue-400 focus:bg-slate-900/80 transition-all duration-300"
                  placeholder="ihre.email@beispiel.de"
                />
              </motion.div>
            </div>

            <motion.div whileFocus={{scale: 1.02}} transition={{type: "spring",stiffness: 300}}>
              <label className="block text-gray-300 font-medium mb-3 flex items-center space-x-2">
                <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-indigo-400" />
                <span>Art des Missbrauchs</span>
              </label>
              <select 
                name="abuseType" 
                value={formData.abuseType} 
                onChange={handleChange} 
                required
                className="w-full bg-slate-950/50 border border-slate-700 rounded-xl px-4 py-4 text-white focus:outline-none focus:border-indigo-400 focus:bg-slate-900/80 transition-all duration-300"
              >
                <option value="">Kategorie wählen</option>
                {abuseTypes.map((type,index)=> (
                  <option key={index} value={type} className="bg-slate-900">
                    {type}
                  </option>
                ))}
              </select>
            </motion.div>

            <motion.div whileFocus={{scale: 1.02}} transition={{type: "spring",stiffness: 300}}>
              <label className="block text-gray-300 font-medium mb-3 flex items-center space-x-2">
                <SafeIcon icon={FiMessageSquare} className="w-5 h-5 text-violet-400" />
                <span>Beschreibung</span>
              </label>
              <textarea 
                name="description" 
                value={formData.description} 
                onChange={handleChange} 
                required 
                rows={6} 
                className="w-full bg-slate-950/50 border border-slate-700 rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-violet-400 focus:bg-slate-900/80 transition-all duration-300 resize-none"
                placeholder="Detaillierte Beschreibung des Vorfalls..."
              />
            </motion.div>

            <motion.div whileFocus={{scale: 1.02}} transition={{type: "spring",stiffness: 300}}>
              <label className="block text-gray-300 font-medium mb-3 flex items-center space-x-2">
                <SafeIcon icon={FiGlobe} className="w-5 h-5 text-emerald-400" />
                <span>Beweise / URLs</span>
              </label>
              <textarea 
                name="evidence" 
                value={formData.evidence} 
                onChange={handleChange} 
                rows={4} 
                className="w-full bg-slate-950/50 border border-slate-700 rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400 focus:bg-slate-900/80 transition-all duration-300 resize-none"
                placeholder="Logs, URLs oder Header-Informationen..."
              />
            </motion.div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-blue-300 text-sm">
              <p className="flex items-center space-x-2">
                <SafeIcon icon={FiMail} className="w-4 h-4 flex-shrink-0" />
                <span>
                  Ihre Meldung wird direkt in Ihrem E-Mail-Programm vorbereitet und an 
                  <span className="font-bold ml-1">abuse@wasacon.net</span> adressiert.
                </span>
              </p>
            </div>

            <motion.button 
              type="submit" 
              disabled={isSubmitting}
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-900/30"
            >
              {isSubmitting ? (
                <>
                  <motion.div 
                    animate={{rotate: 360}}
                    transition={{duration: 1,repeat: Infinity,ease: "linear"}}
                    className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                  />
                  <span>Wird verarbeitet...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSend} className="w-6 h-6" />
                  <span>Bericht Erstellen</span>
                </>
              )}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default ReportForm;